/* RYVENYOU PDP spec shelf.

   The rows under the buy panel. <details> gives the semantics for free -
   keyboard operable, announced correctly, and with a shared `name` the browser
   makes the group exclusive with no JS at all. That is the fallback if this
   file never runs, and it is a good one: everything still opens and closes.

   What <details> will not do is transition height. The browser snaps the panel
   open and shut. So when JS is available we take the toggle over and animate.

   Why `name` is removed on init: the browser enforces exclusivity the moment
   `open` is set on any member of the group, which slams the previously open
   row shut with no animation and leaves our close animation running against an
   already-hidden element. Exclusivity is therefore re-implemented here so the
   outgoing row animates out while the incoming one animates in.

   SHARED FROM 2026-09-10. This used to be one of two near-identical files,
   split from faq.js because faq.js was bound to `.faq__item`, whose skin (white
   card, indicator bar, `[open]` going pure white) lives in the global compiled
   stylesheet and would have landed on these rows. That reason is gone: home's
   FAQ was deleted in 3.82, faq.js is referenced by nothing, and the PDP's FAQ
   uses its own section-scoped `.pfaq__item`. So the selectors are now read off
   the scope element and BOTH accordions on the page run this one file. The
   defaults are the buy column's, so its markup did not change.

   THE WATCHDOG. Completion is what applies the final state - clearing `open`
   after a collapse and removing the inline height. Hanging that solely on
   `onfinish` is not safe: in a throttled or backgrounded renderer the
   animation clock stops and the callback never dispatches, stranding a row
   mid-collapse with `open` still set. A timer therefore races every animation
   and applies the same end state if the event never arrives. */
(function () {
  'use strict';

  var DURATION = 320;
  var EASING = 'cubic-bezier(0.16, 0.84, 0.44, 1)';

  function reduced() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function panelOf(details, panelSel) {
    return details.querySelector(panelSel);
  }

  /* One animation at a time per panel. Anything still running is cancelled
     before the next starts, otherwise a fast double-click leaves two
     animations fighting over the same height. */
  function animate(panel, from, to, done) {
    if (panel._ryAnim) {
      panel._ryAnim.cancel();
      panel._ryAnim = null;
    }
    if (panel._ryTimer) {
      clearTimeout(panel._ryTimer);
      panel._ryTimer = null;
    }

    if (reduced() || typeof panel.animate !== 'function') {
      done();
      return;
    }

    var anim = panel.animate(
      [{ height: from + 'px' }, { height: to + 'px' }],
      { duration: DURATION, easing: EASING }
    );
    panel._ryAnim = anim;

    var settled = false;
    var settle = function () {
      if (settled) return;
      settled = true;
      panel._ryAnim = null;
      if (panel._ryTimer) {
        clearTimeout(panel._ryTimer);
        panel._ryTimer = null;
      }
      done();
    };

    anim.onfinish = settle;
    anim.oncancel = function () { settled = true; };
    panel._ryTimer = setTimeout(settle, DURATION + 90);
  }

  function open(details, panelSel) {
    var panel = panelOf(details, panelSel);
    if (!panel) {
      details.open = true;
      return;
    }
    details.open = true;
    var target = panel.scrollHeight;
    animate(panel, 0, target, function () {
      panel.style.height = '';
    });
  }

  function close(details, panelSel) {
    var panel = panelOf(details, panelSel);
    if (!panel) {
      details.open = false;
      return;
    }
    var start = panel.scrollHeight;
    animate(panel, start, 0, function () {
      panel.style.height = '';
      details.open = false;
    });
  }

  function bind(root) {
    if (root.dataset.ryBound) return;
    root.dataset.ryBound = '1';

    var itemSel = root.getAttribute('data-ry-item') || 'details.pdp__spec';
    var panelSel = root.getAttribute('data-ry-panel') || '.pdp__spec-panel';
    var items = Array.prototype.slice.call(root.querySelectorAll(itemSel));

    items.forEach(function (details) {
      /* Exclusivity moves from the browser to us - see the header note. */
      details.removeAttribute('name');

      var summary = details.querySelector('summary');
      if (!summary) return;

      summary.addEventListener('click', function (event) {
        event.preventDefault();

        if (details.open) {
          close(details, panelSel);
          return;
        }

        items.forEach(function (other) {
          if (other !== details && other.open) close(other, panelSel);
        });
        open(details, panelSel);
      });
    });
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-pdp-specs], [data-ry-accordion]').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Theme editor: a re-rendered section arrives unbound. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
